import type { PageResult } from '@jiaofa/api-types'
import type { AdminRefund } from './order'
import { request } from './request'

export const FINANCE_TRANSACTION_STATUS = {
  Success: 'SUCCESS',
  Processing: 'PROCESSING',
  Failed: 'FAILED',
} as const

/**
 * 对账状态。
 *
 * 当前后端 `FinanceService.reconciliationRows` 只产出这两个值，语义是
 * 「该门店已付款的订单是否全部完结」，**不是资金结算状态**。
 * 设计原型要求的「已打款 / 待打款 / 核对中」三态需要后端先出结算接口，
 * 见 `docs/frontend-profit-sharing-plan.md` 的 P1-5。
 */
export const RECONCILIATION_STATUS = {
  Balanced: 'BALANCED',
  Pending: 'PENDING',
} as const

export type FinanceTransactionStatus = (typeof FINANCE_TRANSACTION_STATUS)[keyof typeof FINANCE_TRANSACTION_STATUS]
export type ReconciliationStatus = (typeof RECONCILIATION_STATUS)[keyof typeof RECONCILIATION_STATUS]

export interface FinanceTransaction {
  id: string
  transactionNo?: string
  orderNo?: string
  channel?: string
  amount: number
  status: FinanceTransactionStatus
  createdAt?: string
}

/**
 * 门店对账行（金额单位：分）。
 *
 * ⚠️ 三个金额字段的真实口径见后端 `FinanceService.reconciliationRows`：
 * - `totalAmount` = 该门店 `payment_status IN (PAID, REFUNDING, REFUNDED)` 且未软删的订单
 *   的 `paid_amount` 合计。注意 **已退款（REFUNDED）订单仍计入**，所以它不是「实收」。
 * - `settledAmount` = 其中**订单状态为 COMPLETED** 的那部分，与资金结算无关
 * - `diffAmount` = 两者之差
 *
 * 也就是说 `settledAmount` 只表示「订单已完结」，不代表钱已经结给门店。
 * 在真正的分账结算接口就位前，UI 文案必须体现这个语义，不能叫「已结算」。
 */
export interface Reconciliation {
  storeId: string
  storeName?: string
  orderCount: number
  totalAmount: number
  settledAmount: number
  diffAmount: number
  status: ReconciliationStatus
}

/**
 * 后端把 `store_id IS NULL` 的订单聚成一行，`storeId` 为字符串 `'0'`、`storeName` 为空串
 * （`FinanceService.toLong` 对非数字返回 0）。直接用 `storeName || storeId` 兜底会把
 * 门店名渲染成裸的 "0"，所以这里统一走这个哨兵值判断。
 */
export const RECONCILIATION_UNASSIGNED_STORE_ID = '0'

export const financeApiReady = true as const

export const fetchFinanceTransactions = async (params: { page?: number; pageSize?: number }): Promise<PageResult<FinanceTransaction>> =>
  request<PageResult<FinanceTransaction>>({
    url: '/finance/transactions',
    method: 'GET',
    params,
  })

export const fetchFinanceRefunds = async (params: { page?: number; pageSize?: number }): Promise<PageResult<AdminRefund>> =>
  request<PageResult<AdminRefund>>({
    url: '/finance/refunds',
    method: 'GET',
    params,
  })

export const fetchReconciliation = async (params: { page?: number; pageSize?: number }): Promise<PageResult<Reconciliation>> =>
  request<PageResult<Reconciliation>>({
    url: '/finance/reconciliation',
    method: 'GET',
    params,
  })
